// ─────────────────────────────────────────────────────────────────────────────
// MSQUARE HOSTINGER & LOCAL CONFIGURATION
// Production: frontend and API on the same origin (https://msquareprofessionals.com/api)
// Local development: React dev server on :3000, Express API on :5000
// ─────────────────────────────────────────────────────────────────────────────

// Google Apps Script web-app URL used by the Contact page. This URL is public by design
// (the browser posts to it directly), so it is not a secret. Override with
// REACT_APP_GOOGLE_SHEET_URL at build time if the script is redeployed.
export const GOOGLE_SHEET_URL =
  process.env.REACT_APP_GOOGLE_SHEET_URL ||
  'https://script.google.com/macros/s/AKfycbwH0TcukMHzXG8KiYuujsQvMwLQviJrZ4AlzuFpYtefpiybQJIcIzMUdtVp2SzRmhGtrg/exec';

// Optional absolute API base, e.g. https://api.example.com/api (only needed when the
// API is hosted on a different origin from the website).
const CONFIGURED_API_BASE = (process.env.REACT_APP_CHATBOT_API_URL || '').trim().replace(/\/+$/, '');

/**
 * Returns prioritized API endpoints for the current environment.
 *
 * @param {string} route - The API route (e.g., '/chat', '/escalation', '/health')
 * @returns {string[]} Endpoint URLs to attempt in order of priority
 */
export function getPlatformApiEndpoints(route = '/chat') {
  const cleanRoute = route.startsWith('/') ? route : `/${route}`;

  if (CONFIGURED_API_BASE) {
    return [`${CONFIGURED_API_BASE}${cleanRoute}`];
  }

  if (typeof window === 'undefined') {
    return [`/api${cleanRoute}`];
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';

  const endpoints = isLocal
    ? [`${window.location.origin}/api${cleanRoute}`, `http://localhost:5000/api${cleanRoute}`]
    : [`${window.location.origin}/api${cleanRoute}`];

  return endpoints.filter((v, i, a) => a.indexOf(v) === i);
}

/**
 * POSTs JSON to the chatbot API.
 * Moves on to the next candidate endpoint only when an endpoint is unreachable or
 * does not return JSON (e.g. a static host returning index.html). A JSON error from
 * the real API (400/429/500...) is returned to the caller as-is so it can be shown.
 *
 * @returns {Promise<{ok: boolean, status: number, data: object|null, errorKind?: string}>}
 */
export async function postToApi(route, payload, { timeoutMs = 35000 } = {}) {
  let lastFailure = { ok: false, status: 0, data: null, errorKind: 'network' };

  for (const endpoint of getPlatformApiEndpoints(route)) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('json')) {
        lastFailure = { ok: false, status: res.status, data: null, errorKind: 'bad_response' };
        continue;
      }
      const data = await res.json().catch(() => null);
      if (!data || typeof data !== 'object') {
        lastFailure = { ok: false, status: res.status, data: null, errorKind: 'bad_response' };
        continue;
      }
      return { ok: res.ok, status: res.status, data };
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return { ok: false, status: 0, data: null, errorKind: 'timeout' };
      }
      lastFailure = { ok: false, status: 0, data: null, errorKind: 'network' };
    } finally {
      clearTimeout(timer);
    }
  }

  return lastFailure;
}

/**
 * Streams a chat answer from POST /api/chat/stream (Server-Sent Events whose `data:` lines are
 * JSON events; plain newline-delimited JSON is also accepted).
 * Calls onDelta(text) as the answer is generated, and resolves with the final "done" event.
 * errorKind 'bad_response' means the server has no streaming endpoint (caller should fall back).
 *
 * @returns {Promise<{ok: boolean, status: number, data: object|null, errorKind?: string, receivedText?: boolean}>}
 */
export async function streamChat(payload, { onDelta, timeoutMs = 35000 } = {}) {
  let lastFailure = { ok: false, status: 0, data: null, errorKind: 'network' };

  for (const endpoint of getPlatformApiEndpoints('/chat/stream')) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let receivedText = false;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream, application/x-ndjson' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const contentType = res.headers.get('content-type') || '';

      const isStream = contentType.includes('event-stream') || contentType.includes('ndjson');
      if (isStream && res.body && res.body.getReader) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalEvent = null;
        const handleLine = (rawLine) => {
          const line = rawLine.replace(/\r$/, '');
          if (!line.trim() || line.startsWith(':')) return; // blank separator / SSE comment
          const payload = line.startsWith('data:') ? line.slice(5).trim() : line;
          let event;
          try {
            event = JSON.parse(payload);
          } catch (e) {
            return;
          }
          if (event.type === 'delta' && event.text) {
            receivedText = true;
            if (onDelta) onDelta(event.text);
          } else if (event.type === 'done' || event.type === 'error') {
            finalEvent = event;
          }
        };
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buffer.indexOf('\n')) >= 0) {
            handleLine(buffer.slice(0, idx));
            buffer = buffer.slice(idx + 1);
          }
        }
        handleLine(buffer);
        if (finalEvent && finalEvent.type === 'done') return { ok: true, status: res.status, data: finalEvent, receivedText };
        if (finalEvent) return { ok: false, status: 500, data: finalEvent, receivedText };
        return { ok: false, status: res.status, data: null, errorKind: 'interrupted', receivedText };
      }

      if (contentType.includes('json')) {
        // Validation / rate-limit errors are plain JSON
        const data = await res.json().catch(() => null);
        if (data) return { ok: res.ok, status: res.status, data };
      }
      lastFailure = { ok: false, status: res.status, data: null, errorKind: 'bad_response' };
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return { ok: false, status: 0, data: null, errorKind: 'timeout', receivedText };
      }
      if (receivedText) return { ok: false, status: 0, data: null, errorKind: 'interrupted', receivedText };
      lastFailure = { ok: false, status: 0, data: null, errorKind: 'network' };
    } finally {
      clearTimeout(timer);
    }
  }
  return lastFailure;
}
